import React from 'react';
import { useState } from "react";
import Swal from "sweetalert2";
import { Button, Row, Col } from "reactstrap";
import { BiSave } from "react-icons/bi";
import { IoIosEye } from "react-icons/io";
import { TiCancel } from "react-icons/ti";
import { AiOutlineHome } from "react-icons/ai";
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayErrorToast, displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import { createMoisture, updateMoisture, getMoistureByPlant } from "../../services/MoistureCorrectionServices";
import { getUserAllowedPlants } from '../../services/PlantServices';
import FloatingControls from '../../components/FloatingControls';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { applyCorrectionFactors, mapMoistureApiToInputs } from './MoistureCorrectionUtils';
import { pickDefaultPlantId } from '../Planning/workScheduleDefaults';
import { getCurrentDateDisplay, getCurrentTime, formatDateForApi } from '../../utils/DateUtils';

const EMPTY_MOISTURE = {
  id: null,
  plant_id: "",
  mc_no: "",
  prefix: "",
  sl_no: "",
  sl_dt: getCurrentDateDisplay(),
  sl_time: getCurrentTime(),
  moisture_msand: 0,
  moisture_csand: 0,
  moisture_rsand: 0,
  moisture_12mm: 0,
  moisture_20mm: 0,
  absorption_msand: 0,
  absorption_csand: 0,
  absorption_rsand: 0,
  absorption_12mm: 0,
  absorption_20mm: 0,
  correction_factor_msand: 0,
  correction_factor_csand: 0,
  correction_factor_rsand: 0,
  correction_factor_12mm: 0,
  correction_factor_20mm: 0,
};

function MoistureCorrection() {
  const [error] = useState({});
  const [isSubmitting] = useState(true);
  const [cookies] = useCookies(['myToken']);
  const [inProgress, setInProgress] = useState(false);
  const [plants, setPlants] = useState([]);
  const [inputs, setInputs] = useState(EMPTY_MOISTURE);
  const navigate = useNavigate();

  const applyPlantMoisture = (plantId, { notifyIfExists = true } = {}) => {
    if (!plantId) {
      setInputs(EMPTY_MOISTURE);
      return;
    }
    setInProgress(true);
    getMoistureByPlant(cookies, plantId)
      .then((response) => {
        setInProgress(false);
        const normalizedPlantId = parseInt(plantId, 10);
        if (!response?.id) {
          setInputs({
            ...EMPTY_MOISTURE,
            plant_id: normalizedPlantId,
            sl_dt: getCurrentDateDisplay(),
            sl_time: getCurrentTime(),
          });
          return;
        }
        if (notifyIfExists) {
          Swal.fire({
            icon: 'info',
            title: 'Record already exists',
            text: 'Moisture correction for this plant already exists. Saving will update it.',
            timer: 2500,
            showConfirmButton: false,
          });
        }
        setInputs(mapMoistureApiToInputs(response));
      })
      .catch((err) => {
        setInProgress(false);
        displayError(err.response?.data ?? err.message, "Failed to load moisture correction");
      });
  };

  React.useEffect(() => {
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = (plantList.plant_list || [])
          .filter((obj) => obj.status)
          .map((plant) => ({ value: plant.id, label: plant.plant_alias || plant.name }));
        setPlants(plantOptions);
        const defaultPlantId = pickDefaultPlantId(plantOptions);
        if (defaultPlantId) {
          applyPlantMoisture(defaultPlantId, { notifyIfExists: false });
        }
      })
      .catch((err) => displayError(err.response?.data ?? err.message, "Failed to load plants"));
  }, [cookies]);

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    try {
      if (name === 'plant_id') {
        applyPlantMoisture(value);
        return;
      }
      const updates = { [name]: value };
      if (name.startsWith('moisture_')) {
        const material = name.replace('moisture_', '');
        const absorption = parseFloat(inputs[`absorption_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (absorption - parseFloat(value || 0)).toFixed(2);
      } else if (name.startsWith('absorption_')) {
        const material = name.replace('absorption_', '');
        const moisture = parseFloat(inputs[`moisture_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (parseFloat(value || 0) - moisture).toFixed(2);
      }
      setInputs((values) => ({ ...values, ...updates }));
    } catch (e) {
      displayErrorToast(e);
    }
  };

  const buildPayload = () => {
    const fields = applyCorrectionFactors({
      id: inputs.id,
      plant_id: parseInt(inputs.plant_id, 10),
      moisture_msand: parseFloat(inputs.moisture_msand) || 0,
      moisture_csand: parseFloat(inputs.moisture_csand) || 0,
      moisture_rsand: parseFloat(inputs.moisture_rsand) || 0,
      moisture_12mm: parseFloat(inputs.moisture_12mm) || 0,
      moisture_20mm: parseFloat(inputs.moisture_20mm) || 0,
      absorption_msand: parseFloat(inputs.absorption_msand) || 0,
      absorption_csand: parseFloat(inputs.absorption_csand) || 0,
      absorption_rsand: parseFloat(inputs.absorption_rsand) || 0,
      absorption_12mm: parseFloat(inputs.absorption_12mm) || 0,
      absorption_20mm: parseFloat(inputs.absorption_20mm) || 0,
    });
    return {
      ...fields,
      plant: fields.plant_id,
      sl_dt: formatDateForApi(inputs.sl_dt),
      sl_time: inputs.sl_time || getCurrentTime(),
      correction_factor_msand: parseFloat(fields.correction_factor_msand) || 0,
      correction_factor_csand: parseFloat(fields.correction_factor_csand) || 0,
      correction_factor_rsand: parseFloat(fields.correction_factor_rsand) || 0,
      correction_factor_12mm: parseFloat(fields.correction_factor_12mm) || 0,
      correction_factor_20mm: parseFloat(fields.correction_factor_20mm) || 0,
    };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!inputs.plant_id) {
      Swal.fire('Please select a plant.', '', 'info');
      return;
    }
    if ((Object.values(error).every((x) => !x)) && isSubmitting) {
      Swal.fire({
        title: 'Do you want to save?',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }).then((result) => {
        if (!result.isConfirmed) {
          Swal.fire('Not saved', '', 'info');
          return;
        }
        const payload = buildPayload();
        const savePromise = inputs.id
          ? updateMoisture(cookies, payload, inputs.id)
          : createMoisture(cookies, payload);
        savePromise
          .then((response) => {
            Swal.fire('Saved!', '', 'success');
            if (response?.id) {
              setInputs((values) => ({
                ...values,
                ...mapMoistureApiToInputs(response),
              }));
            } else {
              applyPlantMoisture(inputs.plant_id);
            }
          })
          .catch((err) => displayError(err.response?.data ?? err.message, "Saved Failed"));
      });
    }
  };

  const Cancel = () => setInputs(EMPTY_MOISTURE);

  const view = () => {
    navigate('/MoistureCorrectionTable');
  };

  const Back = () => {
    navigate('/Home');
  };

  return (
    <>
      <div id="outer-container" className="App">
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <ErrorToast />
        <LoadingOverlay inProgress={inProgress} />
        <div id="page-wrap">
          <form onSubmit={handleSubmit}>
            <div className="container">
              <PremiumPageHeader
                module="QUALITY CONTROL"
                title="Moisture Correction"
                subtitle="Select a plant, then enter moisture and absorption. CF% = Absorption − Moisture. Values apply to DC batch targets and the solar batching report."
                modeLabel="ADD MODE"
              />
            </div>
            <FloatingControls tableLink="/MoistureCorrectionTable" onCancel={Cancel} enableCancel={true} />
            <div className="container">
              <Row>
                <Col xl={12} lg={12} md={12}>
                  <div className="form-row table-bordered shadow p-2 my-2 border-secondary bg-blue form-control-panel">
                    <label htmlFor="plant_id" className="form-group col-sm-2 text-left">Plant</label>
                    <select
                      id="plant_id"
                      name="plant_id"
                      className="form-control col-sm-3 browser-default custom-select mandatory-form-control"
                      required
                      value={inputs.plant_id || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select plant</option>
                      {plants.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>

                    <label htmlFor="mc_no" className="form-group col-sm-2 text-right">MC No</label>
                    <input
                      id="mc_no"
                      name="mc_no"
                      type="text"
                      readOnly
                      className="form-control col-sm-2"
                      value={inputs.mc_no || ''}
                    />

                    <label htmlFor="sl_dt" className="form-group col-sm-1 text-right">Date</label>
                    <input
                      id="sl_dt"
                      name="sl_dt"
                      type="text"
                      className="form-control col-sm-2 mandatory-form-control"
                      placeholder="DD-MM-YYYY"
                      required
                      value={inputs.sl_dt || ''}
                      onChange={handleChange}
                    />

                    <div className="w-100" />
                    <label htmlFor="sl_time" className="form-group col-sm-2 text-right">Time</label>
                    <input
                      id="sl_time"
                      name="sl_time"
                      type="time"
                      className="form-control col-sm-2 mandatory-form-control"
                      required
                      value={inputs.sl_time || ''}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
              </Row>
              <div className="container form-control-panel">
                <div className="form-row align-items-center mb-2">
                  <h5 className="col-sm-6 mb-0"><b>Material Correction</b></h5>
                </div>
                <Row>
                  <Col xl={12} lg={12} md={12}>
                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary bg-blue form-control-panel moisture-correction-form">
                      <div className="table-responsive w-100">
                        <table className="table table-bordered moisture-correction-table mb-0">
                          <thead>
                            <tr>
                              <th className="moisture-correction-col-material">Material</th>
                              <th>Moisture (%)</th>
                              <th>Absorption (%)</th>
                              <th>Correction Factor (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              ['msand', 'MSand'],
                              ['csand', 'CSand'],
                              ['rsand', 'RSand'],
                              ['12mm', '12 mm'],
                              ['20mm', '20 mm'],
                            ].map(([key, label]) => (
                              <tr key={key}>
                                <td className="moisture-correction-material-cell">{label}</td>
                                <td>
                                  <input
                                    type="number"
                                    step="any"
                                    name={`moisture_${key}`}
                                    onChange={handleChange}
                                    value={inputs[`moisture_${key}`] || 0}
                                    max="15"
                                    min="0"
                                    className="form-control mandatory-form-control"
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="any"
                                    name={`absorption_${key}`}
                                    onChange={handleChange}
                                    value={inputs[`absorption_${key}`] || 0}
                                    max="5"
                                    min="0"
                                    className="form-control mandatory-form-control"
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    name={`correction_factor_${key}`}
                                    value={inputs[`correction_factor_${key}`] || 0}
                                    className="form-control moisture-correction-cf-input"
                                    disabled
                                    required
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="footer company-form-footer text-center">
              <div className="company-form-footer-actions">
                <Button type="submit" className="company-form-action-btn">
                  <BiSave size={18} />
                  Save
                </Button>
                <Button type="reset" className="company-form-action-btn" onClick={Cancel}>
                  <TiCancel size={18} />
                  Reset
                </Button>
                <Button type="button" className="company-form-action-btn" onClick={view}>
                  <IoIosEye size={18} />
                  View
                </Button>
                <Button type="button" className="company-form-action-btn" onClick={Back}>
                  <AiOutlineHome size={18} />
                  Home
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default MoistureCorrection;
